import { ExchangeRateService } from "./exchange";
import { CurrencyFormatter } from "./formatter";
import BigMath from "./math";
import type { BigValueType } from "./math";
import { BackendAmount, CurrencyCode, CurrencySymbolDisplay, DecimalDisplayMode, MoneyOptions } from "./types";

export class MoneyFormatBuilder {
    private symbolDisplay?: CurrencySymbolDisplay;
    private decimalDisplay?: DecimalDisplayMode;
    private maxPrecision?: number;

    constructor(
        private service: MoneyService,
        private data: BackendAmount,
        private targetCurrency: CurrencyCode,
        private options?: MoneyOptions,
    ) {}

    showCurrencySymbol( display: CurrencySymbolDisplay ) {
        this.symbolDisplay = display;
        return this;
    }

    /**
     * 参考 axios 的配置合并方式：链式调用只记录覆盖项，最终在 toString 中统一合并。
     * 该设置会覆盖 options.decimalDisplay / options.maxPrecision，但不会覆盖 precision。
     */
    showDecimalPlacesUnlimited() {
        this.decimalDisplay = "unlimited";
        this.maxPrecision = undefined;
        return this;
    }

    /**
     * 参考 axios 的配置合并方式：链式调用只记录覆盖项，最终在 toString 中统一合并。
     * 该设置会覆盖 options.decimalDisplay / options.maxPrecision，但不会覆盖 precision。
     */
    showDecimalPlacesAtMost( maxPrecision: number ) {
        this.decimalDisplay = "max";
        this.maxPrecision = maxPrecision;
        return this;
    }

    private resolveOptions(): MoneyOptions | undefined {
        if (
            this.options === undefined
            && this.symbolDisplay === undefined
            && this.decimalDisplay === undefined
            && this.maxPrecision === undefined
        ) {
            return undefined;
        }

        return {
            ...this.options,
            // 显式链式设置优先于 options 中的 showCurrencySymbol。
            showCurrencySymbol: this.symbolDisplay ?? this.options?.showCurrencySymbol,
            // 动态小数位配置同样遵循链式覆盖 options 的优先级。
            decimalDisplay: this.decimalDisplay ?? this.options?.decimalDisplay,
            maxPrecision: this.maxPrecision ?? this.options?.maxPrecision,
        };
    }

    toString() {
        return this.service.convertAndFormat( this.data, this.targetCurrency, this.resolveOptions() );
    }
}

export class MoneyService {
    constructor( private exchange: ExchangeRateService, private formatter: CurrencyFormatter ) {}

    private normalizeAmountByCurrencyUnit( amount: number, currency: CurrencyCode, targetCurrency: CurrencyCode ) {
        if ( currency === targetCurrency ) {
            return amount;
        }

        if ( currency === "CENT" && targetCurrency === "USD" ) {
            return BigMath.div( amount, 100 );
        }

        if ( currency === "USD" && targetCurrency === "CENT" ) {
            return BigMath.times( amount, 100 );
        }

        return amount;
    }

    /**
     * 加法 (精确)
     */
    add( a: BigValueType, b: BigValueType ): number {
        return BigMath.plus( a, b );
    }

    /**
     * 减法 (精确)
     */
    subtract( a: BigValueType, b: BigValueType ): number {
        return BigMath.minus( a, b );
    }

    /**
     * 乘法 (精确)
     */
    multiply( a: BigValueType, b: BigValueType ): number {
        return BigMath.times( a, b );
    }

    /**
     * 除法 (精确)
     */
    divide( a: BigValueType, b: BigValueType ): number {
        return BigMath.div( a, b );
    }

    /**
     * 转换货币并格式化输出
     * 1. 根据汇率转换金额 (支持关闭)
     * 2. 根据目标货币配置进行格式化 (支持精度、缩放、千分位控制)
     *
     * @param data 后端返回的金额对象 (数值 + 原币种)
     * @param targetCurrency 目标展示币种
     * @param options (可选) 控制选项 (含精度、缩放、千分位、汇率转换)
     */
    convertAndFormat( data: BackendAmount, targetCurrency: CurrencyCode, options?: MoneyOptions ): string {
        const { useExchange = true, ...formatOptions } = options || {};
        const normalizedAmount = this.normalizeAmountByCurrencyUnit( data.amount, data.currency, targetCurrency );

        const shouldUseExchange = useExchange === true && data.currency !== "CENT" && targetCurrency !== "CENT";
        const amountToFormat = shouldUseExchange
            ? this.exchange.convert( normalizedAmount, data.currency, targetCurrency )
            : normalizedAmount;

        return this.formatter.format( amountToFormat, targetCurrency, formatOptions );
    }

    format( data: BackendAmount, targetCurrency: CurrencyCode, options?: MoneyOptions ) {
        return new MoneyFormatBuilder( this, data, targetCurrency, options );
    }
}
